import type { Bounds4, BoxBounds, Point } from 'ag-charts-core';
import { Vec2, Vec4 } from 'ag-charts-core';

import type { AnnotationContext } from '../annotationTypes';
import { AnnotationScene } from '../scenes/annotationScene';
import { ArrowCapScene, type CapScene } from '../scenes/capScene';
import { CollidableLine } from '../scenes/collidableLineScene';
import { CollidableText } from '../scenes/collidableTextScene';
import { DivariantHandle } from '../scenes/handle';
import { StartEndScene } from '../scenes/startEndScene';
import { applySceneNodeTopCenterAnchor } from '../utils/coords';
import { updateLineText } from '../utils/lineWithText';
import { convertLine } from '../utils/values';
import type { LineTypeProperties } from './lineProperties';

export class LineScene extends StartEndScene<LineTypeProperties> {
    static override is(value: unknown): value is LineScene {
        return AnnotationScene.isCheck(value, 'line');
    }

    type = 'line';

    private readonly line = new CollidableLine();
    public text?: CollidableText<never>;
    private startCap?: CapScene;
    private endCap?: CapScene;

    constructor() {
        super();
        this.append([this.line, this.start, this.end]);
    }

    public override update(datum: LineTypeProperties, context: AnnotationContext) {
        let coords = convertLine(datum, context);

        if (coords == null) {
            this.visible = false;
            return;
        }

        coords = Vec4.round(coords);

        this.visible = datum.visible ?? true;
        if (!this.visible) return;

        this.updateLine(datum, coords, context);
        this.updateHandles(datum, coords);
        this.updateText(datum, coords);
        this.updateCaps(datum, coords);
        this.updateAnchor(datum, coords, context);
    }

    private updateLine(datum: LineTypeProperties, coords: Bounds4, context: AnnotationContext) {
        const { line } = this;
        const { lineDashOffset, stroke, strokeWidth, strokeOpacity } = datum;
        const linePoints = this.extendLine(coords, datum, context);

        line.setProperties({
            ...linePoints,
            lineCap: datum.getLineCap(),
            lineDash: datum.getLineDash(),
            lineDashOffset,
            stroke,
            strokeWidth,
            strokeOpacity,
            fillOpacity: 0,
        });
    }

    private updateText(datum: LineTypeProperties, coords: Bounds4) {
        this.text = this.updateNode(CollidableText<never>, this.text, !!datum.text.label);
        updateLineText(this.line.id, this.line, coords, datum.text, this.text, datum.text.label, datum.strokeWidth);
    }

    private updateCaps(datum: LineTypeProperties, coords: Bounds4) {
        if (!datum.startCap && this.startCap) {
            this.startCap.remove();
            this.startCap = undefined;
        }

        if (!datum.endCap && this.endCap) {
            this.endCap.remove();
            this.endCap = undefined;
        }

        if (!datum.startCap && !datum.endCap) return;

        const { stroke, strokeWidth, strokeOpacity } = datum;
        const [start, end] = Vec2.from(coords);
        const angle = Vec2.angle(Vec2.sub(end, start));

        if (datum.startCap) {
            if (this.startCap && this.startCap.type !== datum.startCap) {
                this.startCap.remove();
                this.startCap = undefined;
            }

            if (this.startCap == null) {
                this.startCap = new ArrowCapScene();
                this.append([this.startCap]);
            }

            this.startCap.update({
                x: start.x,
                y: start.y,
                angle: angle - Math.PI,
                stroke,
                strokeWidth,
                strokeOpacity,
            });
        }

        if (datum.endCap) {
            if (this.endCap && this.endCap.type !== datum.endCap) {
                this.endCap.remove();
                this.endCap = undefined;
            }

            if (this.endCap == null) {
                this.endCap = new ArrowCapScene();
                this.append([this.endCap]);
            }

            this.endCap.update({
                x: end.x,
                y: end.y,
                angle,
                stroke,
                strokeWidth,
                strokeOpacity,
            });
        }
    }

    override updateAnchor(_datum: LineTypeProperties, coords: Bounds4, _context: AnnotationContext, _bbox?: BoxBounds) {
        applySceneNodeTopCenterAnchor(this.line, this.anchor, coords);
    }

    override containsPoint(x: number, y: number) {
        const { line, text } = this;
        return super.containsPoint(x, y) || line.isPointInPath(x, y) || Boolean(text?.containsPoint(x, y));
    }

    public override getNodeAtCoords(x: number, y: number): string | undefined {
        if (this.text?.containsPoint(x, y)) return 'text';

        if (this.line.isPointInPath(x, y)) return 'line';

        return super.getNodeAtCoords(x, y);
    }

    protected override getHandleCoords(
        _datum: LineTypeProperties,
        coords: Bounds4,
        handle: 'start' | 'end',
        _bbox?: BoxBounds
    ): Point {
        const { startCap, endCap } = this;

        let [startPoint, endPoint] = Vec2.from(coords);

        // Offset the handles so they do not cover the caps
        const angle = Vec2.angle(Vec2.sub(endPoint, startPoint));
        if (startCap) {
            startPoint = Vec2.rotate(Vec2.from(0, -DivariantHandle.HANDLE_SIZE / 2), angle, startPoint);
        }
        if (endCap) {
            endPoint = Vec2.rotate(Vec2.from(0, DivariantHandle.HANDLE_SIZE / 2), angle, endPoint);
        }

        return handle === 'start' ? startPoint : endPoint;
    }

    protected override getHandleStyles(datum: LineTypeProperties) {
        return {
            fill: datum.handle.fill,
            stroke: datum.handle.stroke ?? datum.stroke,
            strokeOpacity: datum.handle.strokeOpacity ?? datum.strokeOpacity,
            strokeWidth: datum.handle.strokeWidth ?? datum.strokeWidth,
        };
    }
}
